import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import { getLocation } from '$utilities/location.js';

/* ========================================================================= */
/* Functions & hooks */
/* ========================================================================= */

/**
 * @hook useShare
 * @returns {object}
 *     @prop {boolean} isPrintingSupported
 *     @prop {method} printPage
 *
 * 1. Method checking whether or not the function can be executed by the browser.
 * 2. In the browser, checks if the sharing feature is available.
 */
const useShare = url => {
    const sharePage = () => navigator.share({
        title: document.title || 'Event',
        url
    });
    sharePage.isExecutable = () => !!navigator.share; /* [1] */

    const [ isSharingSupported, setIsSharingSupported ] = useState(false);
    useEffect(() => setIsSharingSupported(sharePage.isExecutable()), []); /* [2] */

    return { isSharingSupported, sharePage };
};

/**
 * @hook useShareLinks
 * @param {string} url
 * @returns {object}
 *     @prop {string} twitter
 *     @prop {string} linkedin
 *     @prop {string} facebook
 *     @prop {string} pinterest
 *     @prop {string} mail
 *
 * Generates a list of shareable links for social media and others (eg.: mails).
 */
const useShareLinks = url => !url ? {} : ({
    twitter: `https://twitter.com/intent/tweet?url=${url}&text=`,
    linkedin: `https://www.linkedin.com/shareArticle?mini=true&url=${url}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
    pinterest: `https://pinterest.com/pin/create/button/?url=${url}`,
    email: `mailto:?&subject=&cc=&bcc=&body=${url}%0A`,
});

/**
 * @hook useShareLinks
 * @returns {object}
 *     @prop {function} write
 *     @prop {boolean} isSupported
 *
 * Provides an interface to manage the user’s clipboards.
 */
const useClipboard = () => {
    const [ isClipboardSupported, setIsClipboardSupported ] = useState(false);

    useEffect(() => {
        navigator?.permissions
            ?.query({ name: 'clipboard-write' })
            ?.then(result => {
                if (result.state === 'granted' || result.state === 'prompt') {
                    setIsClipboardSupported(true);
                }
            });
    }, []);

    const write = (value, onSuccess, onFailure) => {
        if (!isClipboardSupported) {
            onFailure();
        }

        navigator.clipboard.writeText(value).then(onSuccess, onFailure);
    };

    return { write, isSupported: isClipboardSupported };
};

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @param {PropTypes.InferProps<typeof propTypes>} props
 */
const ShareLinks = ({ url = getLocation().href }) => {
    // state
    const [ copyLabel, setCopyLabel ] = useState('Copy link');

    // hooks
    const { isSharingSupported } = useShare(url);

    const {
        linkedin,
        twitter
    } = useShareLinks(url);

    const {
        isSupported: isClipboardSupported,
        write: writeOnClipboard
    } = useClipboard();

    // render
    return (

        <section className='pt-25 md:pt-30'>
            {/* Header */}
            <h4 className='type-style-7'>
                Share
            </h4>

            {/* Actions */}
            <ul className='type-style-8 overflow-y-auto flex flex-wrap mt-5'>
                <li className={isClipboardSupported ? '' : 'hide'}>
                    <button
                        className='width-100'
                        onClick={() => (writeOnClipboard(url), setCopyLabel('Copied!'), setTimeout(() => setCopyLabel('Copy link'), 2000))}
                        type='button'>
                        {copyLabel}
                    </button>
                </li>

                <span>&nbsp;·&nbsp;</span>

                <li className={`${!isSharingSupported ? '' : 'hide'} inline`} />

                <li>
                    <a
                        href={linkedin}
                        rel='noreferrer noopener'
                        target='_blank'>
                        LinkedIn
                    </a>
                </li>

                <span>&nbsp;·&nbsp;</span>

                <li>
                    <a
                        href={twitter}
                        rel='noreferrer noopener'
                        target='_blank'>
                        X
                    </a>
                </li>
            </ul>
        </section>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */


const propTypes = {
    url: PropTypes.string,
};

ShareLinks.propTypes = propTypes;

export default ShareLinks;
